// 038 — Trabajo Social (Fase 2 del roadmap de formularios MSP, ver PLAN.md). Hasta
// ahora solo tenía 4 campos de texto libre vía el motor genérico de Questionnaire —
// acá se agrega el resto del instructivo oficial (grupos de atención prioritaria,
// condiciones de riesgo socioeconómico, estructura familiar, situación
// socioeconómica, vivienda, diagnóstico social, evolución del caso). Se convierte a
// Patrón B (discreto) porque el motor de Questionnaire no soporta `EncabezadoMspCampos`
// ni tablas repetibles anidadas — mismo criterio que 054/055, que también conviven con
// un `Questionnaire` "legado" sin usar (038.json queda intacto, nunca se renderiza).
//
// Nota sobre "condiciones de riesgo socioeconómico" (checklist): el instructivo oficial
// lista 13 categorías nombradas + "otros" — PLAN.md lo redondea a "15 ítems", pero sin
// acceso al Excel primario acá no se inventa una categoría adicional sin nombre.
//
// Único contenido que crea recursos FHIR reales: el diagnóstico social (hasta 2
// `Condition`, mismo patrón que Formulario016.tsx). El resto es narrativo — se reporta
// como `Par[]` (PDF + Historia), sin `Observation`s sin consumidor real.
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie, type Valores } from './camposSimplesDatos';
import { SeccionCampos } from './SeccionCampos';
import { calcularSituacionEconomica, type EgresosFamiliares } from './situacionEconomica';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_A_038,
  CAMPOS_RESIDENCIA_038,
  CONDICIONES_RIESGO_038 as CONDICIONES_RIESGO,
  EVOLUCION_VACIA_038 as EVOLUCION_VACIA,
  FAMILIA_VACIA_038 as FAMILIA_VACIA,
  GRUPOS_PRIORITARIOS_038 as GRUPOS_PRIORITARIOS,
  MATERIAL_OPCIONES_038 as MATERIAL_OPCIONES,
  RIESGOS_AMBIENTALES_038 as RIESGOS_AMBIENTALES,
  SERVICIOS_BASICOS_038 as SERVICIOS_BASICOS,
  TENENCIA_OPCIONES_038 as TENENCIA_OPCIONES,
  TIPO_INTERVENCION_OPCIONES_038 as TIPO_INTERVENCION_OPCIONES,
  TIPO_VIVIENDA_OPCIONES_038 as TIPO_VIVIENDA_OPCIONES,
  TOPE_FILAS_038 as TOPE_FILAS,
  paresFormulario038,
  precargaDesdePatient038,
  valoresVaciosA038,
  valoresVaciosResidencia038,
  type EvolucionFila038 as EvolucionFila,
  type FamiliaFila038 as FamiliaFila,
} from './bloques038Datos';

const CODIGO = '038';
const EGRESOS_VACIOS: EgresosFamiliares = { alimentacion: 0, vivienda: 0, serviciosBasicos: 0, educacion: 0, salud: 0, movilizacion: 0, otros: 0 };
// sexo (H/M) y edad son valores cortos/estructurados, no texto libre para dictar; ingresoEconomico es una cifra.
const CAMPOS_FAMILIA_ESTRUCTURADOS = new Set<keyof FamiliaFila>(['sexo', 'edad', 'ingresoEconomico']);

export function Formulario038({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f038.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Trabajo social: el papel no trae servicio y se usa en cualquier nivel.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [motivoIntervencion, setMotivoIntervencion] = useState('');
  const [planSeguimiento, setPlanSeguimiento] = useState('');

  const [datosA, setDatosA] = useState<Valores>(valoresVaciosA038);
  const [residenciaUsuario, setResidenciaUsuario] = useState<Valores>(valoresVaciosResidencia038);
  const [nombreRepresentante, setNombreRepresentante] = useState('');
  const [residenciaRepresentante, setResidenciaRepresentante] = useState<Valores>(valoresVaciosResidencia038);

  // Bloque A (residencia, teléfono, correo, estado civil, etnia, educación, seguro...): se precarga del Patient una sola vez, cuando carga;
  // lo que el usuario ya escribió se respeta.
  const patient = useResource<Patient>(subject);
  const patientPrecargado = useRef(false);
  useEffect(() => {
    if (!patient || patientPrecargado.current) return;
    patientPrecargado.current = true;
    const p = precargaDesdePatient038(patient);
    const sinEscrito = (nuevo: Valores) => (previo: Valores): Valores => ({ ...nuevo, ...Object.fromEntries(Object.entries(previo).filter(([, v]) => v.trim() !== '')) });
    setDatosA(sinEscrito(p.datosA));
    setResidenciaUsuario(sinEscrito(p.residenciaUsuario));
    setResidenciaRepresentante(sinEscrito(p.residenciaRepresentante));
    setNombreRepresentante((previo) => previo.trim() || p.nombreRepresentante);
  }, [patient]);

  const [gruposPrioritarios, setGruposPrioritarios] = useState<Record<string, boolean>>({});
  const [condicionesRiesgo, setCondicionesRiesgo] = useState<Record<string, boolean>>({});

  const [familia, setFamilia] = useState<FamiliaFila[]>([]);

  const [ingresos, setIngresos] = useState<number | ''>('');
  const [egresos, setEgresos] = useState<EgresosFamiliares>(EGRESOS_VACIOS);
  const situacionEconomica = calcularSituacionEconomica(ingresos === '' ? 0 : ingresos, egresos);

  const [tenencia, setTenencia] = useState('');
  const [tipoVivienda, setTipoVivienda] = useState('');
  const [numeroCuartos, setNumeroCuartos] = useState<number | ''>('');
  const [numeroBanos, setNumeroBanos] = useState<number | ''>('');
  const [personasPorDormitorio, setPersonasPorDormitorio] = useState<number | ''>('');
  const [numeroCamas, setNumeroCamas] = useState<number | ''>('');
  const [materialConstructivo, setMaterialConstructivo] = useState('');
  const [serviciosBasicos, setServiciosBasicos] = useState<Record<string, boolean>>({});
  const [riesgosAmbientales, setRiesgosAmbientales] = useState<Record<string, boolean>>({});

  const [diagnostico1, setDiagnostico1] = useState<DiagnosticoCie10Valor>({ texto: '', cie10: null });
  const [diagnostico2, setDiagnostico2] = useState<DiagnosticoCie10Valor>({ texto: '', cie10: null });

  const [evolucion, setEvolucion] = useState<EvolucionFila[]>([]);

  function CampoSelect({
    label,
    valor,
    opciones,
    prefijoOpcionesI18n,
    onChange,
  }: {
    label: string;
    valor: string;
    opciones: readonly string[];
    prefijoOpcionesI18n: string;
    onChange: (v: string) => void;
  }): JSX.Element {
    return (
      <div className="flex flex-col gap-1">
        <Label>{label}</Label>
        <Select value={valor || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {opciones.map((op) => (
              <SelectItem key={op} value={op}>
                {t(`${prefijoOpcionesI18n}.${op}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = paresFormulario038(t, {
        motivoIntervencion,
        planSeguimiento,
        datosA,
        residenciaUsuario,
        nombreRepresentante,
        residenciaRepresentante,
        gruposPrioritarios,
        condicionesRiesgo,
        familia,
        ingresos,
        egresos,
        situacionEconomica,
        vivienda: { tenencia, tipo: tipoVivienda, material: materialConstructivo, numeroCuartos, numeroBanos, personasPorDormitorio, numeroCamas, serviciosBasicos, riesgosAmbientales },
        evolucion,
      });

      const lote = new LoteTransaccional();
      if (diagnostico1.texto.trim()) {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(diagnostico1), recordedDate: new Date().toISOString() });
        pares.push({ pregunta: t('formularios.f038.diagnostico1'), respuesta: conCodigoCie(diagnostico1) });
      }
      if (diagnostico2.texto.trim()) {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(diagnostico2), recordedDate: new Date().toISOString() });
        pares.push({ pregunta: t('formularios.f038.diagnostico2'), respuesta: conCodigoCie(diagnostico2) });
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionDatosAmpliados')}</h4>
      <SeccionCampos prefijoId="f038-a" campos={CAMPOS_A_038} valores={datosA} onChange={setDatosA} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionGruposPrioritarios')}</h4>
      <ChecklistGrid claves={GRUPOS_PRIORITARIOS} prefijoI18n="formularios.f038.gruposPrioritarios" prefijoId="grupos" valores={gruposPrioritarios} onChange={setGruposPrioritarios} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionCondicionesRiesgo')}</h4>
      <ChecklistGrid claves={CONDICIONES_RIESGO} prefijoI18n="formularios.f038.condicionesRiesgo" prefijoId="riesgo" valores={condicionesRiesgo} onChange={setCondicionesRiesgo} />

      <AreaDictado id="f038-motivo-intervencion" etiqueta={t('formularios.f038.motivoIntervencion')} valor={motivoIntervencion} onChange={setMotivoIntervencion} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionResidenciaUsuario')}</h4>
      <SeccionCampos prefijoId="f038-res-usuario" campos={CAMPOS_RESIDENCIA_038} valores={residenciaUsuario} onChange={setResidenciaUsuario} />

      <CampoDictado id="f038-representante" etiqueta={t('formularios.f038.nombreRepresentante')} valor={nombreRepresentante} onChange={setNombreRepresentante} />
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionResidenciaRepresentante')}</h4>
      <SeccionCampos prefijoId="f038-res-representante" campos={CAMPOS_RESIDENCIA_038} valores={residenciaRepresentante} onChange={setResidenciaRepresentante} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionEstructuraFamiliar')}</h4>
      <div className="flex flex-col gap-3">
        {familia.map((f, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(Object.keys(FAMILIA_VACIA) as (keyof FamiliaFila)[]).map((campo) =>
                CAMPOS_FAMILIA_ESTRUCTURADOS.has(campo) ? (
                  <div key={campo} className="flex flex-col gap-1">
                    <Label>{t(`formularios.f038.estructuraFamiliar.${campo}`)}</Label>
                    <Input value={f[campo]} onChange={(e) => setFamilia((prev) => prev.map((row, idx) => (idx === i ? { ...row, [campo]: e.target.value } : row)))} />
                  </div>
                ) : (
                  <CampoDictado
                    key={campo}
                    id={`f038-familia-${i}-${campo}`}
                    etiqueta={t(`formularios.f038.estructuraFamiliar.${campo}`)}
                    valor={f[campo]}
                    onChange={(v) => setFamilia((prev) => prev.map((row, idx) => (idx === i ? { ...row, [campo]: v } : row)))}
                  />
                ),
              )}
            </div>
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setFamilia((prev) => prev.filter((_, idx) => idx !== i))}>
              <Trash2 className="size-4" /> {t('formularios.f038.estructuraFamiliar.quitarIntegrante')}
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={familia.length >= TOPE_FILAS} onClick={() => setFamilia((prev) => [...prev, { ...FAMILIA_VACIA }])}>
          {t('formularios.f038.estructuraFamiliar.agregarIntegrante')}
        </Button>
        {familia.length >= TOPE_FILAS && <p className="text-xs text-hce-muted">{t('formularios.f038.estructuraFamiliar.topeAlcanzado')}</p>}
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionSituacionEconomica')}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.situacionEconomica.totalIngresos')}</Label>
          <Input type="number" min={0} value={ingresos} onChange={(e) => setIngresos(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        {(Object.keys(EGRESOS_VACIOS) as (keyof EgresosFamiliares)[]).map((rubro) => (
          <div key={rubro} className="flex flex-col gap-1">
            <Label>{t(`formularios.f038.situacionEconomica.egreso${rubro.charAt(0).toUpperCase()}${rubro.slice(1)}`)}</Label>
            <Input type="number" min={0} value={egresos[rubro] || ''} onChange={(e) => setEgresos((prev) => ({ ...prev, [rubro]: e.target.value === '' ? 0 : Number(e.target.value) }))} />
          </div>
        ))}
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.situacionEconomica.totalEgresos')}</Label>
          <Input value={situacionEconomica.totalEgresos} disabled />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.situacionEconomica.disponibilidad')}</Label>
          <Input value={situacionEconomica.disponibilidad} disabled />
        </div>
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionVivienda')}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <CampoSelect label={t('formularios.f038.vivienda.tenencia')} valor={tenencia} opciones={TENENCIA_OPCIONES} prefijoOpcionesI18n="formularios.f038.vivienda.tenenciaOpciones" onChange={setTenencia} />
        <CampoSelect label={t('formularios.f038.vivienda.tipo')} valor={tipoVivienda} opciones={TIPO_VIVIENDA_OPCIONES} prefijoOpcionesI18n="formularios.f038.vivienda.tipoOpciones" onChange={setTipoVivienda} />
        <CampoSelect
          label={t('formularios.f038.vivienda.materialConstructivo')}
          valor={materialConstructivo}
          opciones={MATERIAL_OPCIONES}
          prefijoOpcionesI18n="formularios.f038.vivienda.materialOpciones"
          onChange={setMaterialConstructivo}
        />
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.vivienda.numeroCuartos')}</Label>
          <Input type="number" min={0} value={numeroCuartos} onChange={(e) => setNumeroCuartos(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.vivienda.numeroBanos')}</Label>
          <Input type="number" min={0} value={numeroBanos} onChange={(e) => setNumeroBanos(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.vivienda.personasPorDormitorio')}</Label>
          <Input type="number" min={0} value={personasPorDormitorio} onChange={(e) => setPersonasPorDormitorio(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f038.vivienda.numeroCamas')}</Label>
          <Input type="number" min={0} value={numeroCamas} onChange={(e) => setNumeroCamas(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
      </div>
      <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f038.vivienda.seccionServiciosBasicos')}</h5>
      <ChecklistGrid claves={SERVICIOS_BASICOS} prefijoI18n="formularios.f038.vivienda.serviciosBasicos" prefijoId="servicios" valores={serviciosBasicos} onChange={setServiciosBasicos} />
      <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f038.vivienda.seccionRiesgosAmbientales')}</h5>
      <ChecklistGrid claves={RIESGOS_AMBIENTALES} prefijoI18n="formularios.f038.vivienda.riesgosAmbientales" prefijoId="ambientales" valores={riesgosAmbientales} onChange={setRiesgosAmbientales} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionDiagnosticoSocial')}</h4>
      <SelectorDiagnosticoCie10 label={t('formularios.f038.diagnostico1')} value={diagnostico1} onChange={setDiagnostico1} />
      <SelectorDiagnosticoCie10 label={t('formularios.f038.diagnostico2')} value={diagnostico2} onChange={setDiagnostico2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionEvolucionCaso')}</h4>
      <div className="flex flex-col gap-3">
        {evolucion.map((e, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label>{t('formularios.f038.evolucionCaso.fecha')}</Label>
                <Input type="date" value={e.fecha} onChange={(ev) => setEvolucion((prev) => prev.map((row, idx) => (idx === i ? { ...row, fecha: ev.target.value } : row)))} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('formularios.f038.evolucionCaso.hora')}</Label>
                <Input type="time" value={e.hora} onChange={(ev) => setEvolucion((prev) => prev.map((row, idx) => (idx === i ? { ...row, hora: ev.target.value } : row)))} />
              </div>
              <CampoSelect
                label={t('formularios.f038.evolucionCaso.tipoIntervencion')}
                valor={e.tipoIntervencion}
                opciones={TIPO_INTERVENCION_OPCIONES}
                prefijoOpcionesI18n="formularios.f038.evolucionCaso.tipoIntervencionOpciones"
                onChange={(v) => setEvolucion((prev) => prev.map((row, idx) => (idx === i ? { ...row, tipoIntervencion: v } : row)))}
              />
              <div className="col-span-full">
                <AreaDictado
                  id={`f038-evolucion-${i}-problemas`}
                  etiqueta={t('formularios.f038.evolucionCaso.problemasIdentificados')}
                  valor={e.problemasIdentificados}
                  onChange={(v) => setEvolucion((prev) => prev.map((row, idx) => (idx === i ? { ...row, problemasIdentificados: v } : row)))}
                  filas={2}
                />
              </div>
              <div className="col-span-full">
                <AreaDictado
                  id={`f038-evolucion-${i}-plan`}
                  etiqueta={t('formularios.f038.evolucionCaso.planIntervencion')}
                  valor={e.planIntervencion}
                  onChange={(v) => setEvolucion((prev) => prev.map((row, idx) => (idx === i ? { ...row, planIntervencion: v } : row)))}
                  filas={2}
                />
              </div>
            </div>
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setEvolucion((prev) => prev.filter((_, idx) => idx !== i))}>
              <Trash2 className="size-4" /> {t('formularios.f038.evolucionCaso.quitarEntrada')}
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={evolucion.length >= TOPE_FILAS} onClick={() => setEvolucion((prev) => [...prev, { ...EVOLUCION_VACIA }])}>
          {t('formularios.f038.evolucionCaso.agregarEntrada')}
        </Button>
        {evolucion.length >= TOPE_FILAS && <p className="text-xs text-hce-muted">{t('formularios.f038.evolucionCaso.topeAlcanzado')}</p>}
      </div>

      <AreaDictado id="f038-plan-seguimiento" etiqueta={t('formularios.f038.planSeguimiento')} valor={planSeguimiento} onChange={setPlanSeguimiento} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f038.seccionProfesionalResponsable')}</h4>
      <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
